/**
 * Client-side copy of the suggestCalibreLineage curator-value guard (lineage
 * Phase 5). The server copy is `guardLineageCuratorValues` in
 * functions/src/calibreLineageSuggest.ts; lineageSuggestionParity.test.ts runs
 * both over the same fixtures so they cannot drift.
 *
 * Why twice (the #723 description-link pattern): the server guards against the
 * values it read, the admin guards against the values on screen — a curator may
 * have edited a revision between the run and the review, and a stale function
 * deploy must not be the only line of defence.
 */
import type {
  CustomCalibreEntry,
  LineageCuratorConflict,
  LineageSuggestionMember,
  SuggestCalibreLineageResult,
} from './firebase';

/** The lineage facts the guard reads (a subset of CustomCalibreEntry). */
export type GuardCalibre = Pick<
  CustomCalibreEntry,
  | 'id'
  | 'calibreLineageId'
  | 'revisionLabel'
  | 'activeFromYear'
  | 'activeUntilYear'
  | 'activeDatesSource'
  | 'isCurrent'
  | 'isLineagePlaceholder'
  | 'activeDatesConfidence'
>;

const sameLabel = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

function yearsText(from?: number, until?: number): string {
  if (typeof from !== 'number' && typeof until !== 'number') return '';
  return `${from ?? '?'}–${until ?? ''}`;
}

/** Withhold every proposal that would change or clear a curator-set lineage value. Non-mutating. */
export function guardLineageSuggestion(
  s: SuggestCalibreLineageResult,
  calibres: readonly GuardCalibre[],
  lineageId: string | null,
): SuggestCalibreLineageResult {
  const byId = new Map(calibres.map((c) => [c.id, c]));
  const conflicts: LineageCuratorConflict[] = [...s.curatorConflicts];
  const members: LineageSuggestionMember[] = [];

  for (const m of s.members) {
    const c = byId.get(m.calibreId);
    if (!c) continue;
    if (c.calibreLineageId && c.calibreLineageId !== lineageId) {
      conflicts.push({ calibreId: c.id, field: 'calibreLineageId', curatorValue: c.calibreLineageId, proposedValue: lineageId });
      continue;
    }
    const kept: LineageSuggestionMember = { ...m };
    if (c.isLineagePlaceholder) {
      if (kept.revisionLabel !== undefined || kept.activeFromYear !== undefined || kept.activeUntilYear !== undefined) {
        conflicts.push({
          calibreId: c.id,
          field: 'isLineagePlaceholder',
          curatorValue: true,
          proposedValue: kept.revisionLabel ?? yearsText(kept.activeFromYear, kept.activeUntilYear),
        });
      }
      delete kept.revisionLabel;
      delete kept.activeFromYear;
      delete kept.activeUntilYear;
    }
    if (kept.revisionLabel !== undefined && c.revisionLabel && !sameLabel(kept.revisionLabel, c.revisionLabel)) {
      conflicts.push({ calibreId: c.id, field: 'revisionLabel', curatorValue: c.revisionLabel, proposedValue: kept.revisionLabel });
      delete kept.revisionLabel;
    }
    const curatorYears = c.activeDatesSource !== 'ai';
    for (const field of ['activeFromYear', 'activeUntilYear'] as const) {
      const stored = c[field];
      const proposed = kept[field];
      if (curatorYears && typeof stored === 'number' && proposed !== undefined && proposed !== stored) {
        conflicts.push({ calibreId: c.id, field, curatorValue: stored, proposedValue: proposed });
        delete kept[field];
      }
    }
    members.push(kept);
  }

  let current = s.current;
  if (current) {
    const picked = byId.get(current.calibreId);
    const storedCurrent = lineageId
      ? calibres.find((c) => c.isCurrent && !c.isLineagePlaceholder && c.calibreLineageId === lineageId)
      : undefined;
    if (picked?.isLineagePlaceholder) {
      conflicts.push({ calibreId: picked.id, field: 'isLineagePlaceholder', curatorValue: true, proposedValue: 'current' });
      current = null;
    } else if (picked?.calibreLineageId && picked.calibreLineageId !== lineageId) {
      conflicts.push({ calibreId: picked.id, field: 'calibreLineageId', curatorValue: picked.calibreLineageId, proposedValue: lineageId });
      current = null;
    } else if (storedCurrent && storedCurrent.id !== current.calibreId) {
      conflicts.push({ calibreId: storedCurrent.id, field: 'isCurrent', curatorValue: true, proposedValue: current.calibreId });
      current = null;
    }
  }

  // Every further line is a NEW line: guarded with no lineage id, so a member
  // already in any line (this run's included) is withheld, never moved.
  const additionalLines = (s.additionalLines ?? []).map((l) => {
    const g = guardLineageSuggestion(
      { ...s, members: l.members, current: l.current, curatorConflicts: l.curatorConflicts, additionalLines: [] },
      calibres,
      null,
    );
    return { ...l, members: g.members, current: g.current, curatorConflicts: g.curatorConflicts };
  });

  return { ...s, members, current, curatorConflicts: conflicts, additionalLines };
}

/**
 * The server already withheld some proposals and the client guard may withhold
 * more; the same withheld item must be listed once.
 */
export function dedupeConflicts(conflicts: readonly LineageCuratorConflict[]): LineageCuratorConflict[] {
  const seen = new Set<string>();
  return conflicts.filter((c) => {
    const k = `${c.calibreId}|${c.field}|${String(c.proposedValue)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

const FIELD_LABEL: Record<LineageCuratorConflict['field'], string> = {
  calibreLineageId: 'belongs to another line',
  revisionLabel: 'revision label',
  activeFromYear: 'active from',
  activeUntilYear: 'active until',
  isCurrent: 'current revision',
  isLineagePlaceholder: 'placeholder',
};

/** One human line per withheld proposal, for the dialog's "kept your values" note. */
export function describeConflict(c: LineageCuratorConflict, nameOf: (id: string) => string): string {
  const name = nameOf(c.calibreId);
  switch (c.field) {
    case 'calibreLineageId':
      return `${name} ${FIELD_LABEL[c.field]} (${String(c.curatorValue)}) — left out`;
    case 'isCurrent':
      return `${name} stays the current revision (AI suggested ${nameOf(String(c.proposedValue))})`;
    case 'isLineagePlaceholder':
      return `${name} is a placeholder — AI's ${c.proposedValue === 'current' ? 'current flag' : `"${String(c.proposedValue)}"`} not applied`;
    default:
      return `${name} keeps ${FIELD_LABEL[c.field]} ${String(c.curatorValue)} (AI suggested ${String(c.proposedValue)})`;
  }
}

/** What applying one row writes — the input to `setCalibreRevision`. */
export interface AppliedRevisionFields {
  calibreLineageId: string;
  revisionLabel?: string;
  activeFromYear?: number;
  activeUntilYear?: number;
  isCurrent?: boolean;
  isLineagePlaceholder?: boolean;
  activeDatesSource?: 'ai';
  activeDatesConfidence?: 'high' | 'medium' | 'low';
}

/**
 * Merge one approved member row onto the calibre as stored NOW. The write-side
 * half of the guard: whatever the suggestion says, a stored label, curator
 * years, the current flag and the placeholder flag come through unchanged, and
 * an omitted field is "no change" — never a clear.
 *
 * Years are stamped `activeDatesSource: 'ai'` only when no curator year is left
 * on the record. A curator from-year with an AI until-year stays unstamped, so
 * the pair is curator-owned from then on: stamping it would let a later
 * suggestion revise the curator's year.
 */
export function revisionFieldsForMember(
  stored: GuardCalibre,
  lineageId: string,
  row: Pick<LineageSuggestionMember, 'revisionLabel' | 'activeFromYear' | 'activeUntilYear' | 'confidence'>,
): AppliedRevisionFields {
  const placeholder = stored.isLineagePlaceholder === true;
  const aiOwned = stored.activeDatesSource === 'ai';
  const pick = (field: 'activeFromYear' | 'activeUntilYear'): { value?: number; fromAi: boolean } => {
    const s = stored[field];
    const p = placeholder ? undefined : row[field];
    if (typeof s === 'number' && !aiOwned) return { value: s, fromAi: false };
    if (p !== undefined) return { value: p, fromAi: true };
    return { value: s, fromAi: aiOwned && typeof s === 'number' };
  };
  const from = pick('activeFromYear');
  const until = pick('activeUntilYear');
  const anyYear = from.value !== undefined || until.value !== undefined;
  const curatorYearLeft = (from.value !== undefined && !from.fromAi) || (until.value !== undefined && !until.fromAi);
  const label = stored.revisionLabel || (placeholder ? undefined : row.revisionLabel);
  return {
    calibreLineageId: lineageId,
    ...(label ? { revisionLabel: label } : {}),
    ...(from.value !== undefined ? { activeFromYear: from.value } : {}),
    ...(until.value !== undefined ? { activeUntilYear: until.value } : {}),
    ...(stored.isCurrent ? { isCurrent: true } : {}),
    ...(placeholder ? { isLineagePlaceholder: true } : {}),
    ...(anyYear && !curatorYearLeft ? { activeDatesSource: 'ai' as const, activeDatesConfidence: row.confidence } : {}),
  };
}

/** Applying the "current revision" row: every stored fact kept, plus the current flag. */
export function revisionFieldsForCurrent(stored: GuardCalibre, lineageId: string): AppliedRevisionFields {
  return {
    calibreLineageId: lineageId,
    ...(stored.revisionLabel ? { revisionLabel: stored.revisionLabel } : {}),
    ...(typeof stored.activeFromYear === 'number' ? { activeFromYear: stored.activeFromYear } : {}),
    ...(typeof stored.activeUntilYear === 'number' ? { activeUntilYear: stored.activeUntilYear } : {}),
    isCurrent: true,
    ...(stored.activeDatesSource === 'ai'
      ? { activeDatesSource: 'ai' as const, ...(stored.activeDatesConfidence ? { activeDatesConfidence: stored.activeDatesConfidence } : {}) }
      : {}),
  };
}
